import type { FluxErrors } from './errors.js';

export type FluxErrorCategories = {
  [key in keyof typeof FluxErrors]: (typeof FluxErrors)[key] extends { code: `${infer Category}/${string}` } ? Category : never;
}[keyof typeof FluxErrors];

export interface FluxErrorCategoryMetadata {
  description: string;
  group?: FluxErrorCategories;
  localizations: Record<string, { description: string; name: string }>;
  name: string;
}

/**
 * Objeto com todas as categorias de erro disponíveis no Flux. Cada categoria
 * possui uma descrição geral, um nome amigável e, opcionalmente, um grupo ao qual
 * pertence (para categorizações mais amplas).
 */
export const FLUX_ERROR_CATEGORIES: Record<FluxErrorCategories, FluxErrorCategoryMetadata> = {
  account: {
    description: 'Erros relacionados à conta',
    localizations: {},
    name: 'Conta',
  },
  attachment: {
    description: 'Falhas durante o upload, download ou processamento de arquivos anexados.',
    localizations: {},
    name: 'Anexo',
  },
  automation: {
    description: 'Erros gerais na criação, salvamento ou gestão de regras de automação.',
    group: 'automation',
    localizations: {},
    name: 'Automação',
  },
  'automation-action': {
    description: 'Falhas na execução de uma ação específica dentro de uma regra de automação.',
    group: 'automation',
    localizations: {},
    name: 'Ação de Automação',
  },
  'automation-v2': {
    description: 'Erros gerais no sistema de automação de segunda geração.',
    group: 'automation-v2',
    localizations: {},
    name: 'Automação V2',
  },
  'automation-v2-action': {
    description: 'Falhas na execução de uma ação na nova versão (V2) do sistema de automação.',
    group: 'automation-v2',
    localizations: {},
    name: 'Ação de Automação V2',
  },
  'automation-v2-autocomplete': {
    description: 'Erros ao tentar buscar dados para autocompletar em configurações de automação V2.',
    group: 'automation-v2',
    localizations: {},
    name: 'Autocompletar V2',
  },
  'automation-v2-execution': {
    description: 'Problemas na inicialização ou andamento da execução de uma automação V2.',
    group: 'automation-v2',
    localizations: {},
    name: 'Execução de Automação V2',
  },
  'automation-v2-execution-action': {
    description: 'Falhas durante a execução de uma ação específica em uma automação V2.',
    group: 'automation-v2',
    localizations: {},
    name: 'Execução de Ação V2',
  },
  'automation-v2-execution-action-generic': {
    description: 'Falhas gerais na execução de uma ação específica em uma automação V2.',
    group: 'automation-v2',
    localizations: {},
    name: 'Execução de Ação V2',
  },
  'automation-v2-role': {
    description: 'Erros relacionados às permissões ou regras associadas à automação V2.',
    group: 'automation-v2',
    localizations: {},
    name: 'Regra/Permissão de Automação V2',
  },
  'automation-v2-trigger': {
    description: 'Erros na configuração ou disparo de um evento que inicia uma automação V2.',
    group: 'automation-v2',
    localizations: {},
    name: 'Gatilho de Automação V2',
  },
  'automation-v2-trigger-condition': {
    description: 'Falha na avaliação ou definição de uma condição que dispara uma automação V2.',
    group: 'automation-v2',
    localizations: {},
    name: 'Condição de Gatilho V2',
  },
  'automation-v2-version': {
    description: 'Problemas ao gerenciar ou atualizar versões de regras de automação V2.',
    group: 'automation-v2',
    localizations: {},
    name: 'Versão de Automação V2',
  },
  'bulk-action': {
    description: 'Erros relacionados à execução de ações em massa (bulk actions) na plataforma.',
    localizations: {},
    name: 'Ação em Massa',
  },
  card: {
    description: 'Erros gerais na criação, edição ou manipulação de um card (ex: em um kanban).',
    group: 'card',
    localizations: {},
    name: 'Card',
  },
  'card-assignee': {
    description: 'Falhas ao atribuir ou remover um responsável em um card.',
    group: 'card',
    localizations: {},
    name: 'Responsável pelo Card',
  },
  'card-attachment': {
    description: 'Problemas ao anexar ou gerenciar arquivos diretamente em um card.',
    group: 'card',
    localizations: {},
    name: 'Anexo do Card',
  },
  'card-comment': {
    description: 'Erros na criação, edição ou exclusão de comentários em um card.',
    group: 'card',
    localizations: {},
    name: 'Comentário do Card',
  },
  'card-comment-attachment': {
    description: 'Falhas ao adicionar ou gerenciar anexos em comentários de cartões.',
    group: 'card',
    localizations: {},
    name: 'Anexo do Comentário',
  },
  'card-review': {
    description: 'Falhas relacionadas ao processo de revisão ou aprovação de um card.',
    group: 'review',
    localizations: {},
    name: 'Revisão do Card',
  },
  'card-solicitation': {
    description: 'Falhas relacionadas ao processo de solicitação para criar ou alterar um card.',
    localizations: {},
    name: 'Solicitação de Card',
  },
  'card-task': {
    description: 'Falhas na criação, edição ou conclusão de subtarefas dentro de um card.',
    group: 'card-task',
    localizations: {},
    name: 'Tarefa do Card',
  },
  'card-task-assignee': {
    description: 'Erros ao atribuir ou remover um responsável em uma subtarefa de card.',
    group: 'card-task',
    localizations: {},
    name: 'Responsável pela Tarefa',
  },
  'card-task-label': {
    description: 'Erros ao aplicar ou remover etiquetas em uma subtarefa de card.',
    group: 'card-task',
    localizations: {},
    name: 'Etiqueta da Tarefa',
  },
  'cast-value': {
    description: 'Erros relacionados à conversão de valores entre tipos ou formatos.',
    localizations: {},
    name: 'Conversão de Valor',
  },
  database: {
    description: 'Erros de conectividade ou operações de nível superior no banco de dados.',
    group: 'database',
    localizations: {},
    name: 'Banco de Dados',
  },
  'database-id-lookup': {
    description: 'Falhas ao tentar localizar um registro específico usando identificadores de outras entidades ligadas ao banco de dados. Esse é um serviço auxiliar a checagem de permissões.',
    group: 'database',
    localizations: {},
    name: 'Busca por ID do Banco de Dados',
  },
  'database-member': {
    description: 'Erros relacionados à gestão de membros com acesso ao banco de dados.',
    group: 'database',
    localizations: {},
    name: 'Membro do Banco de Dados',
  },
  'database-record': {
    description: 'Falhas ao criar, ler, atualizar ou excluir registros (linhas) específicos.',
    group: 'database',
    localizations: {},
    name: 'Registro de Banco de Dados',
  },
  'database-schema': {
    description: 'Problemas na estrutura, definição ou migração do esquema do banco de dados.',
    group: 'database',
    localizations: {},
    name: 'Esquema de Banco de Dados',
  },
  deadline: {
    description: 'Erros ao definir, modificar ou validar datas de prazo.',
    localizations: {},
    name: 'Prazo / Data Limite',
  },
  feature: {
    description: 'Erros que afetam o funcionamento de uma característica ou módulo específico.',
    localizations: {},
    name: 'Funcionalidade',
  },
  'feature-flag': {
    description: 'Erros na gestão ou avaliação das bandeiras de ativação/desativação de recursos.',
    group: 'feature-flag',
    localizations: {},
    name: 'Flag de Funcionalidade',
  },
  'feature-flag-account': {
    description: 'Falhas ao aplicar ou verificar bandeiras de funcionalidades em nível de conta.',
    group: 'feature-flag',
    localizations: {},
    name: 'Flag de Funcionalidade (Conta)',
  },
  field: {
    description: 'Falhas na manipulação (criação, validação) de um campo de dados de um formulário.',
    localizations: {},
    name: 'Campo do Formulário',
  },
  form: {
    description: 'Erros no envio, renderização ou validação de dados de um formulário.',
    localizations: {},
    name: 'Formulário',
  },
  label: {
    description: 'Erros gerais na gestão de etiquetas (criação, renomeação, exclusão).',
    localizations: {},
    name: 'Etiqueta',
  },
  mfa: {
    description: 'Problemas com códigos, configuração ou validação do MFA (Multi-Factor Authentication).',
    group: 'sign-in',
    localizations: {},
    name: 'Autenticação de Múltiplos Fatores',
  },
  notification: {
    description: 'Erros ao gerar, enviar ou exibir notificações aos usuários.',
    group: 'notification',
    localizations: {},
    name: 'Notificação',
  },
  'notification-preference': {
    description: 'Falhas ao salvar ou aplicar as configurações de preferência de notificação do usuário.',
    group: 'notification',
    localizations: {},
    name: 'Preferência de Notificação',
  },
  'password-reset': {
    description: 'Erros no processo de solicitação ou conclusão da redefinição de senha.',
    group: 'sign-in',
    localizations: {},
    name: 'Redefinição de Senha',
  },
  permission: {
    description: 'Erros de acesso (usuário não autorizado a realizar a ação).',
    localizations: {},
    name: 'Permissão / Autorização',
  },
  pipe: {
    description: 'Erros na gestão ou visualização de um fluxo ou fluxo de trabalho completo.',
    group: 'pipe',
    localizations: {},
    name: 'Fluxo',
  },
  'pipe-id-lookup': {
    description: 'Falhas ao tentar localizar um fluxo específico usando identificadores de outras entidades ligadas ao fluxo. Esse é um serviço auxiliar a checagem de permissões.',
    group: 'pipe',
    localizations: {},
    name: 'Busca por ID do Fluxo',
  },
  'recovery-code': {
    description: 'Falhas na validação ou uso de códigos de recuperação de conta.',
    group: 'sign-in',
    localizations: {},
    name: 'Código de Recuperação',
  },
  review: {
    description: 'Erros gerais na gestão ou submissão de revisões no sistema.',
    group: 'review',
    localizations: {},
    name: 'Revisão',
  },
  reviewer: {
    description: 'Erros relacionados à seleção, atribuição ou ação de um revisor.',
    group: 'review',
    localizations: {},
    name: 'Revisor',
  },
  role: {
    description: 'Erros ao manipular funções de usuário e suas permissões.',
    localizations: {},
    name: 'Papéis',
  },
  'sign-in': {
    description: 'Erros que impedem o usuário de iniciar sessão na aplicação.',
    group: 'sign-in',
    localizations: {},
    name: 'Login / Acesso',
  },
  stage: {
    description: 'Erros na gestão de etapas dentro de um fluxo de trabalho (ex: kanban column).',
    group: 'stage',
    localizations: {},
    name: 'Etapa',
  },
  'stage-card-form': {
    description: 'Falhas no formulário usado para mover ou atualizar um card em uma etapa.',
    group: 'stage',
    localizations: {},
    name: 'Formulário da Etapa do Card',
  },
  'stage-form': {
    description: 'Falhas relacionadas a formulários específicos de uma determinada etapa.',
    group: 'stage',
    localizations: {},
    name: 'Formulário da Etapa',
  },
  'sync-automation': {
    description: 'Problemas na execução ou configuração de automações síncronas.',
    group: 'automation',
    localizations: {},
    name: 'Automações Síncronas',
  },
  template: {
    description: 'Falhas na criação, uso ou aplicação de modelos pré-definidos.',
    group: 'template',
    localizations: {},
    name: 'Modelos',
  },
  'template-review': {
    description: 'Erros no processo de revisão ou aprovação de um modelo.',
    group: 'template',
    localizations: {},
    name: 'Modelo de Revisão',
  },
  upload: {
    description: 'Falhas gerais no processo de envio de arquivos para o servidor.',
    localizations: {},
    name: 'Transferência de Arquivo (Upload)',
  },
  user: {
    description: 'Erros gerais na gestão de usuários (criação, edição, busca).',
    group: 'user',
    localizations: {},
    name: 'Usuário',
  },
  'user-invite': {
    description: 'Falhas no envio, aceitação ou gestão de convites para novos usuários.',
    group: 'user',
    localizations: {},
    name: 'Convite de Usuário',
  },
  'user-license': {
    description: 'Erros relacionados à verificação ou gestão da licença de uso do usuário.',
    localizations: {},
    name: 'Licença de Usuário',
  },
  validation: {
    description: 'Falhas devido a dados de entrada incorretos, incompletos ou inválidos.',
    localizations: {},
    name: 'Validação de Dados',
  },
  workflow: {
    description: 'Erros na definição, execução ou transição de estados de um fluxo de trabalho.',
    localizations: {},
    name: 'Fluxo de Trabalho',
  },
};
